import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Shield, Eye, EyeOff, User, Building } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import encryptData from '../config/encryptConfig';
import i18n from '../i18n';
import ResetPasswordModal from '../components/ResetPasswordModal';
function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);


  // Reset Password Modal state
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  // Get the return URL from location state
  const from = location.state?.from || null;

  const handleChange = (e) => {
    setCredentials((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(credentials.username.trim(), credentials.password.trim());
      if (result.success) {
        // Navigate to the originally requested page or default based on role

        if (i18n.language === 'mr') {
          i18n.changeLanguage('en');
          document.documentElement.lang = 'en';
        }
        if (from) {
          navigate(from, { replace: true });
        } else {
          // Navigate based on user role
          const userRole = result.user?.role?.toUpperCase();
          if (userRole === 'COLLECTOR' || userRole === 'ADMIN') {
            navigate('/admin');
          } else if (userRole === 'OFFICER' || userRole === 'TALUKA_OFFICER') {
            navigate('/dashboard');
          } else {
            // Default fallback
            navigate('/dashboard');
          }
        }
      } else {
        setError(

          t('login.error') ||
          'Invalid credentials. Please try again.'
        );
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(
        t('login.error') || 'Login failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-lg w-full">
          {/* Government Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center shadow-lg">
                <Shield className="w-8 h-8" style={{ color: '#B8860B' }} />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {t('login.GovernmentPortal') || 'Government Officer Portal'}
            </h1>
            <p className="text-gray-600 text-lg mb-3">
              {t('login.subtitle') || 'Login for Government Staff Only'}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-800 via-blue-600 to-blue-800 mx-auto mt-4"></div>
          </div>

          {/* Login Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
            {/* Card Header */}
            <div
              className="px-8 py-6"
              style={{
                background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 100%)',
              }}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Building className="w-6 h-6 text-white" />
                <h2 className="text-xl font-semibold text-white">
                  {t('login.GovernmentPortal')}
                </h2>
              </div>
              <p className="text-yellow-100 text-sm text-center">
                {t('login.governmentportalnote')}
              </p>
            </div>

            {/* Form Content */}
            <div className="px-8 py-8">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-md p-4 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-red-800 font-medium">
                      {error || 'Invalid credentials. Please try again.'}
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4 text-gray-500" />
                    {t('login.username') || 'User Name'}
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="input"
                    required
                    value={credentials.username}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 transition-colors duration-200 bg-gray-50 focus:bg-white"
                    style={{
                      '--tw-ring-color': '#B8860B',
                      borderColor: '#B8860B',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#B8860B')}
                    placeholder={t('login.notfillusername')}
                  />
                </div>

                {/* Password Field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"
                  >
                    <Lock className="w-4 h-4 text-gray-500" />
                    {t('login.password') || 'Password'}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={credentials.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 transition-colors duration-200 bg-gray-50 focus:bg-white pr-12"
                      style={{
                        '--tw-ring-color': '#B8860B',
                        borderColor: '#B8860B',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = '#B8860B')}
                      placeholder={t('login.notfillpassword')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Security Notice */}
                {/* <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-amber-800 font-semibold mb-1">
                        {t('login.GovernmentStaffAccessOnly')}
                      // </p>
                      <p className="text-xs text-amber-700">
                         {t('login.accessnote')}
                      </p>
                    </div>
                  </div>
                </div> */}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{t('login.signing') || 'Signing in...'}</span>
                    </div>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Shield className="w-5 h-5" />
                      {t('login.signin') || 'Sign In'}
                    </span>
                  )}
                </button>

                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setIsResetModalOpen(true)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 border-b border-blue-600"
                  >
                    {t('login.forgotPassword') || 'Forgot Password?'}
                  </button>

                </div>
                {/* {/* Reset Password Modal */}
                <ResetPasswordModal
                  isOpen={isResetModalOpen}
                  onClose={() => setIsResetModalOpen(false)}
                />
              </form>
            </div>
          </div>

          {/* Citizens Redirect */}
          <div className="mt-6 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl shadow-lg text-white p-6 text-center">
            <h3 className="font-bold text-lg mb-2">
              {t('login.AreyouaCitizen')}
            </h3>
            <p className="text-green-50 mb-4">{t('login.citizennote')}</p>
            <a
              href="/"
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              style={{ color: '#B8860B' }}
            >
              <span>{t('login.citizenportal')}</span>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </a>
          </div>

          {/* Demo Credentials
          <div className="mt-8 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 overflow-hidden">
            <div className="bg-yellow-50 px-6 py-3 border-b border-yellow-100">
              <h3 className="font-semibold flex items-center gap-2" style={{color: '#B8860B'}}>
                <User className="w-5 h-5" />
                {t('login.demo.title') || 'Demo Staff Login Credentials:'}
              </h3>
            </div>
            <div className="p-6 grid gap-4 sm:grid-cols-2">
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-lg border border-yellow-200">
                <div className="font-medium mb-1" style={{color: '#B8860B'}}>Department Officer</div>
                <div className="text-sm" style={{color: '#DAA520'}}>officer@gadchiroli.gov.in</div>
                <div className="text-sm" style={{color: '#CD853F'}}>officer123</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                <div className="font-medium text-green-900 mb-1">Taluka Officer</div>
                <div className="text-sm text-green-700">to.gadchiroli@gov.in</div>
                <div className="text-sm text-green-600">to123</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                <div className="font-medium text-purple-900 mb-1">District Collector</div>
                <div className="text-sm text-purple-700">collector@gadchiroli.gov.in</div>
                <div className="text-sm text-purple-600">collector123</div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                <div className="font-medium text-orange-900 mb-1">System Admin</div>
                <div className="text-sm text-orange-700">admin@gadchiroli.gov.in</div>
                <div className="text-sm text-orange-600">admin123</div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
